import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostBinding, Input } from '@angular/core';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';
import { MuiAnimationTiming } from '../utility';
import { DEFAULT_MUI_TOAST_INDIVIDUAL_CONFIG, MuiToastConfig } from './mui-toast-config';


@Component({
  selector: 'mui-toast',
  templateUrl: 'mui-toast.component.html',
  animations: [
    trigger('flyInOut', [
      state(
        'inactive',
        style({
          opacity: 0,
          transform: 'translateY(-2rem)'
        })
      ),
      transition('inactive => active', [animate(MuiAnimationTiming.EASE_OUT_SHORT)]),
      transition(':leave', [
        animate(
          MuiAnimationTiming.EASE_IN_SHORT,
          style({
            opacity: 0,
            transform: 'translateY(-2rem)'
          })
        ),
        animate(MuiAnimationTiming.EASE_SHORT, style({ height: 0, marginTop: 0 }))
      ])
    ])
  ],
  preserveWhitespaces: false
})
export class MuiToast extends Toast {

  private static uid = 0;

  readonly uid = ++MuiToast.uid;

  /*
   * options variable needs to be initialized here, because of how typescript 4.7+ works.
   *  If a variable with the same name exists both in the child and parent classes, we can either initialize straight away or
   * use the "declare" keyword.
   */
  @Input() options: MuiToastConfig = {
    /*
     *  Start default values from IndividualConfig that are required to be initialized, but not provided by the user.
     */
    enableHtml: false,
    titleClass: 'toast-title',
    messageClass: 'toast-message',
    easing: 'ease-in',
    onActivateTick: false,
    newestOnTop: true,
    payload: null,
    /*
     *  End default values.
     */
    // toastType is the default value passed in MuiToastService
    toastType: 'info',
    ...DEFAULT_MUI_TOAST_INDIVIDUAL_CONFIG
  };

  @HostBinding('attr.role') get role(): string {
    if (this.options?.hasAction) {
      return 'alertdialog';
    }
    return 'alert';
  }

  @HostBinding('attr.aria-labelledby') readonly ariaLabelledBy = this.labelId;
  @HostBinding('attr.aria-describedby') readonly ariaDescribedby = this.labelId + '-desc';

  @HostBinding('attr.aria-live') readonly ariaLive = 'polite';

  @HostBinding('class.mui-toast--success') get isSuccessToast(): boolean {
    return this.options.toastType === 'success';
  }

  @HostBinding('class.mui-toast--warning') get isWarningToast(): boolean {
    return this.options.toastType === 'warning';
  }

  @HostBinding('class.mui-toast--danger') get isDangerToast(): boolean {
    return this.options.toastType === 'danger';
  }

  @HostBinding('class.mui-toast--info') get isInfoToast(): boolean {
    return this.options.toastType === 'info';
  }

  @HostBinding('class.mui-toast--dismissible') get isDismissible(): boolean {
    return this.options.tapToDismiss;
  }


  constructor(protected toastrService: ToastrService, public toastPackage: ToastPackage) {
    super(toastrService, toastPackage);

    // Override the default options with the ones passed in the toastPackage
    this.options = { ...(toastPackage.config as MuiToastConfig) };
  }

  get labelId(): string | null {
    return `toast-label-${this.uid ?? ''}`;
  }

  action(e: Event): boolean {
    e.stopPropagation();
    this.toastPackage.triggerAction();
    return false;
  }
}
